import { useEffect, useState } from "react";
import axios from "axios";
import { Brain, Share2 } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REALM_META = {
  marketing: { label: "Marketing", color: "#FF4F27" },
  designer:  { label: "Designing", color: "#8B5CF6" },
  cgi:       { label: "CGI",       color: "#3B82F6" },
  movie:     { label: "Movie",     color: "#F59E0B" },
};

const SIGNAL_LABEL = {
  style:        "style",
  music_genre:  "music vibe",
  genre:        "genre",
  tone:         "tone",
  voice:        "voice",
  polish_mode:  "polish",
  aspect_ratio: "aspect",
  duration:     "duration",
  kling_model:  "model",
  length:       "length",
  effect:       "effect",
};

/**
 * Brain Recap card — a soft "look what XEA learned about you" surface
 * pinned to the dashboard. Surfaces 2–3 personal-taste insights with a
 * one-tap share-to-clipboard so users can post their evolving style
 * elsewhere. Renders nothing until the brain has enough data to be
 * meaningful (≥ 3 events). Color-bound to whichever realm has the most
 * events so the card always feels native.
 */
export default function BrainRecapCard() {
  const { token } = useAuth();
  const [brain, setBrain] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!token) return;
      try {
        const r = await axios.get(`${API}/brain/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancel) {
          setBrain(r.data || null);
          setLoaded(true);
        }
      } catch (_e) {
        if (!cancel) setLoaded(true);
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  if (!loaded) return null;
  const total = brain?.total_events || 0;
  if (total < 3) return null;
  const perRealm = brain?.per_realm || {};
  if (Object.keys(perRealm).length === 0) return null;

  // Pick the dominant realm + extract top insights
  const realms = Object.entries(perRealm)
    .map(([r, b]) => ({ realm: r, events: b.total_events || 0, tops: b.tops || {} }))
    .sort((a, b) => b.events - a.events);

  const top = realms[0];
  const meta = REALM_META[top.realm] || { label: top.realm, color: "#737373" };

  // Build up to 3 insight lines from the dominant realm
  const insightOrder = ["style", "genre", "tone", "music_genre", "polish_mode", "voice", "aspect_ratio", "duration"];
  const insights = insightOrder
    .filter((k) => top.tops[k]?.[0])
    .slice(0, 3)
    .map((k) => ({ key: k, label: SIGNAL_LABEL[k] || k, value: top.tops[k][0] }));

  const shareText = `🧠 What XEA learned about my creative taste this week:\n` +
    insights.map((i) => `· ${i.label}: ${i.value}`).join("\n") +
    `\n\nBuilt on xe-a.com — try it.`;

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: "What XEA learned about me", text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
      }
    } catch (_e) { /* user cancelled */ }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="rounded-2xl border p-5"
      style={{ borderColor: `${meta.color}33`, background: `${meta.color}08` }}
      data-testid="brain-recap-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: `${meta.color}22` }}
          >
            <Brain className="w-5 h-5" style={{ color: meta.color }} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: meta.color }}>
              {meta.label} · {top.events} {top.events === 1 ? "event" : "events"} learned
            </div>
            <h3 className="text-lg font-bold text-ink-950 mt-0.5">Your XEA brain · weekly recap</h3>
            <p className="text-xs text-ink-500">Here&apos;s what XEA noticed about your taste.</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleShare}
          className="text-xs shrink-0"
          data-testid="brain-recap-share"
        >
          <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
        </Button>
      </div>
      <ul className="mt-4 space-y-2" data-testid="brain-recap-insights">
        {insights.map((i) => (
          <li key={i.key} className="flex items-baseline gap-2 text-sm">
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: meta.color }}
            />
            <span className="text-ink-700">You lean into</span>
            <span className="text-ink-950 font-bold">{i.value}</span>
            <span className="text-ink-500 text-xs uppercase tracking-[0.10em]">· {i.label}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}
